import { supabase } from "@/infrastructure/supabase/client";
import { featureRequestAttachmentsRepository } from "@/infrastructure/repositories/feature-request-attachments.supabase.repository";
import type { FeatureRequestRow } from "@/infrastructure/supabase/database.types";

export interface CreateFeatureRequestInput {
  title: string;
  description: string | null;
  type: FeatureRequestRow["type"];
  submitted_by: string;
  submitter_email: string | null;
}

// source_groups arrives with migration 0150; a row read before it gets an empty list, so
// every row that reaches the list cache has the field.
function toRow(r: FeatureRequestRow): FeatureRequestRow {
  return { ...r, source_groups: (r.source_groups as string[] | undefined) ?? [] };
}

export const featureRequestsRepository = {
  async list(): Promise<FeatureRequestRow[]> {
    const { data, error } = await supabase
      .from("feature_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw error;
    return ((data as FeatureRequestRow[] | null) ?? []).map(toRow);
  },

  async create(input: CreateFeatureRequestInput): Promise<FeatureRequestRow> {
    const { data, error } = await supabase
      .from("feature_requests")
      .insert({
        title: input.title,
        description: input.description,
        type: input.type,
        submitted_by: input.submitted_by,
        submitter_email: input.submitter_email,
      })
      .select("*")
      .single();
    if (error) throw error;
    return toRow(data as FeatureRequestRow);
  },

  async update(
    id: string,
    patch: Partial<Pick<FeatureRequestRow, "title" | "description" | "type" | "status">>
  ): Promise<FeatureRequestRow> {
    const { data, error } = await supabase
      .from("feature_requests")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return toRow(data as FeatureRequestRow);
  },

  async remove(id: string): Promise<void> {
    // Clear the Storage objects first — the FK cascade drops the attachment rows,
    // which would otherwise strand the bytes in the private bucket (best-effort).
    await featureRequestAttachmentsRepository.removeAllForRequest(id);
    const { error } = await supabase.from("feature_requests").delete().eq("id", id);
    if (error) throw error;
  },
};
